// The first frame of a new commander (player-reach.md §8.5, R2127).
//
// While the welcome card is open, the card is the whole first frame: the
// toasts, the achievement cards and the season title card wait, and the
// legend card and the rows of the sea trials stay folded. When the card
// closes (TAKE COMMAND or SEA TRIALS), the waiting items play in order,
// one at a time, with `WELCOME_RELEASE_GAP_MS` between two, so the
// player reads each one and no frame stacks them.
//
// The hold is a queue of closures and keeps no copy of what it holds. A
// caller asks `defer` first and draws itself when the answer is false.
// Three rules:
//
//  1. **A key keeps one item.** The season title card hangs off two
//     events of the arrival, so its second call replaces the first.
//  2. **A loose item is a toast, and the hold keeps the newest few.** A
//     card that stands for minutes collects toasts that are stale when it
//     closes; `WELCOME_HELD_NOTICES` of them play, and the INBOX keeps
//     the record of every one.
//  3. **The rank orders the release.** The title card plays first, then
//     the toasts in their order, then the line of the handover grades,
//     then the legend opens. The hooks of `watch` run while the hold still
//     stands, so what a hook says at the release joins the queue. The
//     welcome card states the founding truce, so the release repeats it
//     in no toast (R2138).

/** What one waiting item plays, and where it stands in the release. */
interface Held {
  key: string;
  rank: number;
  seq: number;
  run: () => void;
}

/** The two moments a watcher hears. */
export interface HoldWatch {
  /** The hold starts: the card rose. */
  hold?(): void;
  /** The hold ends: the card closed. The hold still stands while this
   *  runs, so a `defer` here joins the release. */
  release?(): void;
}

/** The ranks of the release, first to last. */
export const RANK = { title: 0, notice: 1, handover: 2, fold: 3 } as const;

export class FirstHold {
  private on = false;
  private items: Held[] = [];
  private seq = 0;
  private readonly watchers: HoldWatch[] = [];
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();

  /**
   * `gapMs` stands between two items of the release, and `looseMax` is
   * how many loose items (the toasts) the hold keeps.
   */
  constructor(private readonly gapMs: number, private readonly looseMax: number) {}

  /** True while the welcome card is open. */
  get holding(): boolean { return this.on; }

  /** How many items wait. A test reads it. */
  get waiting(): number { return this.items.length; }

  /** Adds a watcher of the two moments. */
  watch(w: HoldWatch): void { this.watchers.push(w); }

  /** Starts the hold. A second call while it stands does nothing. */
  hold(): void {
    if (this.on) return;
    this.on = true;
    for (const w of this.watchers) w.hold?.();
  }

  /**
   * Keeps `run` for the release while the hold stands, and answers true;
   * answers false and keeps nothing when no hold stands. An empty `key`
   * is a loose item (rule 2); a key replaces the item it names (rule 1).
   */
  defer(key: string, rank: number, run: () => void): boolean {
    if (!this.on) return false;
    if (key !== '') this.items = this.items.filter((h) => h.key !== key);
    this.items.push({ key, rank, seq: this.seq++, run });
    if (key === '') {
      const loose = this.items.filter((h) => h.key === '');
      if (loose.length > this.looseMax) this.items.splice(this.items.indexOf(loose[0]), 1);
    }
    return true;
  }

  /**
   * Ends the hold and plays what waits: the first item one gap after the
   * close, so the card has left, and each next item one gap later.
   */
  release(): void {
    if (!this.on) return;
    for (const w of this.watchers) w.release?.();
    this.on = false;
    const out = [...this.items].sort((a, b) => a.rank - b.rank || a.seq - b.seq);
    this.items = [];
    out.forEach((h, i) => {
      const t = setTimeout(() => {
        this.timers.delete(t);
        h.run();
      }, (i + 1) * this.gapMs);
      this.timers.add(t);
    });
  }

  /** Forgets every item and timer. For a teardown. */
  destroy(): void {
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
    this.items = [];
    this.on = false;
  }
}
